import { Constants } from "./constants";
import { StringUtils } from "./string-utils";
import { NumberUtils } from "./number-utils";
import { parseAsTwoValuesAndGetSecond, getProductName, getCustomFieldValueParsed, getPricing, parseValue } from "./product-model";
import { documentToDict } from "./algolia-utils";
import { RouteUtils } from "./route-utils";

export var ProductDecorator = function (options) {
    options = options || {};
    this.options = options;

    initialize(this);
};

ProductDecorator.prototype.getWarranty = function () {
    if (this.customDict?.warranty)
        return this.customDict.warranty;

    if (this.product?.warranty)
        return this.product.warranty;

    return "";
};

ProductDecorator.prototype.getParsedWarranty = function () {
    let warranty = this.getWarranty();
    if (!warranty)
        return {};

    let parsedWarranty = StringUtils.jsonTryParse(warranty);
    return parsedWarranty || {};
};

ProductDecorator.prototype.getPartSdkProductLine = function () {
    let productLineValue = "";
    if (this.customDict)
        productLineValue = this.customDict[Constants.ALGOLIA.PRODUCT.PART_SDK_PRODUCT_LINE] || "";

    if (productLineValue)
        return productLineValue;

    let warranty = this.getParsedWarranty();
    if (!warranty)
        return "";

    let productLine = warranty[Constants.ALGOLIA.PRODUCT.PART_SDK_PRODUCT_LINE] || "";
    productLineValue = parseAsTwoValuesAndGetSecond(productLine);
    return productLineValue;
};

ProductDecorator.prototype.getPartProductLineIfUnderSpecialValidation = function () {
    var productLineValue = this.getPartSdkProductLine();
    if (Constants.PRODUCT_LINES_WITH_SPECIAL_VALIDATION.includes(productLineValue))
        return productLineValue;

    return "";
};

ProductDecorator.prototype.getType = function () {
    if (this.customDict) {
        if (this.customDict.type)
            return this.customDict.type;

        if (this.customDict.node) {
            let type = getCustomFieldValueParsed(this.customDict.node.customFields?.edges?.map(p => p.node), Constants.ALGOLIA.PRODUCT.TYPE);
            if (type)
                return type;
        }
    }
    if (this.product) {
        if (this.product.type)
            return this.product.type;

        if (this.product.node) {
            let type = getCustomFieldValueParsed(this.product.node.customFields?.edges?.map(p => p.node), Constants.ALGOLIA.PRODUCT.TYPE);
            if (type)
                return type;

        }
        if (this.product.customFields) {
            let type = getCustomFieldValueParsed(this.product.customFields?.edges?.map(p => p.node), Constants.ALGOLIA.PRODUCT.TYPE);
            if (type)
                return type;
        }
    }

    return "";
};

ProductDecorator.prototype.isNewEquipment = function () {
    return this.getType() === Constants.PRODUCT.TYPES.NEW_EQUIPMENT;
};

ProductDecorator.prototype.isPart = function () {
    return this.getType() === Constants.PRODUCT.TYPES.PARTS;
};

ProductDecorator.prototype.isUsedEquipment = function () {
    return this.getType() === Constants.PRODUCT.TYPES.USED_EQUIPMENT;
};

ProductDecorator.prototype.isEquipment = function () {
    return this.getType().indexOf(Constants.PRODUCT.TYPES.EQUIPMENT) > -1;
};

ProductDecorator.prototype.hasReplacementPart = function () {
    return this.getReplacementPart() !== "";
};

ProductDecorator.prototype.getModel = function () {
    let model = "";
    if (this.customDict)
        model = this.customDict[Constants.ALGOLIA.PRODUCT.MODEL] || "";

    if (model)
        return model;

    let warranty = this.getParsedWarranty();
    if (!warranty)
        return "";

    let modelLine = warranty[Constants.ALGOLIA.PRODUCT.MODEL] || "";
    model = parseAsTwoValuesAndGetSecond(modelLine);
    return model;
};

ProductDecorator.prototype.getBrandName = function () {
    if (this.customDict?.brand) {
        if (this.customDict.brand.name)
            return this.customDict.brand.name;

        return this.customDict.brand;
    }

    if (this.product?.brand?.name)
        return this.product.brand.name;

    return "";
};

ProductDecorator.prototype.getProductName = function () {
    if (this.customDict) {
        if (this.customDict.name)
            return getProductName(this.customDict.name, this.getType(), this.customDict.brand?.name);
        if (this.customDict.node) {
            return getProductName(this.customDict.node.name, this.getType(), this.customDict.node.brand?.name)
        }
    }
    if (this.product) {
        if (this.product.name)
            return getProductName(this.product.name, this.getType(), this.product.brand?.name);
        if (this.product.node)
            return getProductName(this.product.node.name, this.getType(), this.product.node.brand?.name);
    }

    return "";
};

ProductDecorator.prototype.isVermeer = function () {
    if (this.getBrandName() === Constants.PRODUCT.BRANDS.VERMEER)
        return true;

    return false;
};

ProductDecorator.prototype.isJohnDeere = function () {
    if (this.getBrandName() === Constants.PRODUCT.BRANDS.JOHN_DEERE)
        return true;

    return false;
};

ProductDecorator.prototype.getPrices = function () {
    if (this.customDict) {
        if (this.customDict.prices)
            return this.customDict.prices;

        if (this.customDict.price_sort_bc || this.customDict.suggested_retail_price || this.customDict.price_sale) {
            var pricingObject = {};

            var price = NumberUtils.tryParseFloat(this.customDict.price_sort_bc);
            if (price || price === 0)
                pricingObject.price = { value: price };

            var retailPrice = NumberUtils.tryParseFloat(this.customDict.suggested_retail_price);
            if (retailPrice || retailPrice === 0)
                pricingObject.retailPrice = { value: retailPrice };

            var salePrice = NumberUtils.tryParseFloat(this.customDict.price_sale);
            if (salePrice || salePrice === 0)
                pricingObject.salePrice = { value: salePrice };

            return pricingObject;
        }
    }

    if (this.product?.prices)
        return this.product.prices;

    return null;
};

ProductDecorator.prototype.getPricingDetails = function () {
    var pricingObject = this.getPrices();
    var pricing = getPricing(pricingObject?.price?.value, pricingObject?.retailPrice?.value, pricingObject?.salePrice?.value);
    return pricing;
};

ProductDecorator.prototype.shouldShowPrice = function (isBusinessCustomer) {
    if (this.isPart() === false) {
        return true;
    } else if (this.hasReplacementPart()) {
        return false;
    }

    let productLineWithSpecialValidation = this.getPartProductLineIfUnderSpecialValidation();
    if (!productLineWithSpecialValidation)
        return true;

    if (Constants.PRODUCT_LINES_WITH_UNRESTRICTED_LOGIN_VALIDATION.includes(productLineWithSpecialValidation) || Constants.PRODUCT_LINES_WITH_99_LOGIN_EXCEPTION_VALIDATION.includes(productLineWithSpecialValidation))
        return true;

    if (isBusinessCustomer)
        return true;

    return false;
};

ProductDecorator.prototype.getJohnDeereVerifyUrl = function () {
    if (!this.isJohnDeere())
        return "";

    if (!this.isPart())
        return "";

    var sku = this.getSku();

    if (sku === "")
        return "";

    var johnDeereVerifyUrl = GlobalApp.generalSettings.johnDeerePartsCatalogUrl;

    johnDeereVerifyUrl = johnDeereVerifyUrl.replace('{0}', sku);

    return johnDeereVerifyUrl;
};

ProductDecorator.prototype.getSku = function () {
    if (this.customDict?.sku)
        return this.customDict.sku.toUpperCase();

    if (this.product?.sku)
        return this.product.sku.toUpperCase();

    return "";
};

ProductDecorator.prototype.getOriginalSku = function () {
    if (this.customDict?.sku)
        return this.customDict.sku;

    if (this.product?.sku)
        return this.product.sku;

    return "";
};

ProductDecorator.prototype.getEntityId = function () {
    var entityId;

    entityId = this.product?.entityId;
    if (entityId)
        return entityId;

    if (this.product?.unique_id) {
        return this.getEntityIdFromUniqueId(this.product?.unique_id);
    }

    entityId = this.customDict.entityId;
    if (entityId)
        return entityId;

    if (this.customDict.unique_id) {
        return this.getEntityIdFromUniqueId(this.customDict.unique_id);
    }

    return null;
};

ProductDecorator.prototype.getEntityIdFromUniqueId = function (uniqueId) {
    if (uniqueId.startsWith('bc_')) {
        return uniqueId.replace('bc_', '');
    }
    else {
        return uniqueId;
    }
};

ProductDecorator.prototype.updateItemPrice = function (pricingInfo) {
    if (this.product) {
        Vue.set(this.product, 'prices', pricingInfo);
        return true;
    }

    if (this.customDict) {
        if (this.options.Document) {
            this.options.Document.prices = [pricingInfo];
            initialize(this);
        } else {
            this.customDict.prices = pricingInfo;
        }

        return true;
    }

    return false;
}

ProductDecorator.prototype.getCta = function (fieldValue, isLoggedIn, requestQuote) {
    if (typeof fieldValue !== "string")
        return false;

    let cta = { text: fieldValue, openInNewTab: false };
    cta.isAddToCart = (fieldValue === Constants.ALGOLIA.PRODUCT.CTA.VALUE_ADD_TO_CART || (fieldValue === Constants.ALGOLIA.PRODUCT.CTA.VALUE_BACKORDERED && isLoggedIn));

    let replacementPart = this.getReplacementPart();
    if (replacementPart) {
        cta.href = `${GlobalApp.generalSettings.productDetailsPageUrl}${replacementPart[0].path}`;
        cta.text = `Replaced by ${replacementPart[0].sku}`;
        cta.isAddToCart = false;
        return cta;
    }

    if (cta.isAddToCart)
        return cta;

    let ctaHref = Constants.PRODUCT.SKU_SEPARATOR;
    switch (fieldValue) {
        case Constants.ALGOLIA.PRODUCT.CTA.VALUE_BUILDER_LINK:
            var warranty = this.getParsedWarranty();
            var linkUrl = parseValue(warranty[Constants.ALGOLIA.PRODUCT.CTA.KEY_BUILDER_LINK]);
            if (linkUrl) {
                ctaHref = linkUrl;
                cta.openInNewTab = true;
            }
            break;
        case Constants.ALGOLIA.PRODUCT.CTA.VALUE_REQUEST_QUOTE:
            if (requestQuote) {
                ctaHref = `${requestQuote?.LinkUrl}?${Constants.PRODUCT.QUERY_PARAMETERS.PRODUCT_ID}=${this.getEntityId()}`;
                cta.text = requestQuote?.Title;
                cta.openInNewTab = requestQuote?.OpenInNewWindow;
            }
            break;
        case Constants.ALGOLIA.PRODUCT.CTA.VALUE_CUSTOM_LINK:
            var warranty = this.getParsedWarranty();
            var linkUrl = parseValue(warranty[Constants.ALGOLIA.PRODUCT.CTA.KEY_CUSTOM_LINK]);
            var linkLabel = parseValue(warranty[Constants.ALGOLIA.PRODUCT.CTA.KEY_CUSTOM_LABEL]);

            if (linkUrl && linkLabel) {
                ctaHref = linkUrl;
                cta.text = linkLabel;
                cta.openInNewTab = true;
            }
            break;
        case Constants.ALGOLIA.PRODUCT.CTA.VALUE_BACKORDERED:
            ctaHref = Constants.ALGOLIA.PRODUCT.CTA.LINK_BACKORDERED;
            cta.text = Constants.ALGOLIA.PRODUCT.CTA.DISPLAY_TEXT_BACKORDERED;
            cta.openInNewTab = false;
            break;
        default:
            break;
    }

    cta.href = ctaHref;
    return cta;
}

ProductDecorator.prototype.getReplacementPart = function () {
    if (this.customDict?.replacement_part) {
        return parseStringToSkuArray(this.customDict.replacement_part);
    }

    if (this.options?.product?.customFields) {
        let replacementPart = getCustomFieldValueParsed(this.options.product.customFields, Constants.ALGOLIA.PRODUCT.PART_REPLACEMENT);
        if (replacementPart)
            return parseStringToSkuArray(replacementPart);
    }

    return "";
};

function parseStringToSkuArray(str) {
    if (!str) return false;

    var arr = str.split('^'),
        decoded_data = [];
    arr.map(el => {
        var onePart = el.split(',');
        if (onePart && onePart.length === 2) {
            decoded_data.push({
                sku: onePart[0],
                path: RouteUtils.ensureNoTrailingSlash(onePart[1])
            });
        }
    });
    return decoded_data.length ? decoded_data : false;
}

function initialize(productDecorator) {
    const options = productDecorator.options;

    productDecorator.customDict = {};
    if (options.customDict) {
        productDecorator.customDict = options.customDict;
    } else if (options.Document) {
        productDecorator.customDict = documentToDict(options.Document);
    };

    if (options.product)
        productDecorator.product = options.product;
}